import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Stack,
  Button,
  Container,
  Typography,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Edit, Trash } from 'react-feather';
import api from '../network/ApiClient';
import Iconify from '../components/iconify';
import CreateDriverForm from './CreateDriverForm';
import { useMerchantFilter, MerchantFilterSelect } from '../components/merchant-filter';
import { withoutPlaceholder } from '../helpers/placeholder';

export default function DriversPage() {
  const [modalNewDriver, setModalNewDriverShow] = useState(false);
  const [edit, setEdit] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetDriver, setResetDriver] = useState(null);
  const merchantFilter = useMerchantFilter(drivers);

  const columns = [
    { field: 'id', headerName: 'ID', width: 100 },
    { field: 'firstname', headerName: 'First Name', flex: 1 },
    { field: 'lastname', headerName: 'Last Name', flex: 1 },
    { field: 'phone', headerName: 'Phone Number', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    {
      field: 'action',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (cellValues) => (
        <Stack direction="row" spacing={2}>
          <IconButton
            color="primary"
            size="small"
            onClick={() => {
              setEdit(true);
              setSelectedDriver(drivers.find((driver) => driver.id === cellValues.id));
              setModalNewDriverShow(true);
            }}
          >
            <Edit size={15} />
          </IconButton>
          <IconButton
            color="default"
            size="small"
            title="Reset Driver Password"
            onClick={() => handleResetPassword(drivers.find((driver) => driver.id === cellValues.id))}
          >
            <Iconify icon="material-symbols:lock-reset-sharp" width={18} />
          </IconButton>
          <IconButton color="error" size="small" onClick={() => handleDelete(cellValues.id)}>
            <Trash size={15} />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const handleDelete = async (id) => {
    const permission = window.confirm('Do you Want to Delete this Driver ?');
    if (!permission) {
      return;
    }
    try {
      await api.delete(`driver/${id}`);
      setDrivers(drivers.filter((driver) => driver.id !== id));
    } catch (error) {
      console.error('Error deleting driver:', error);
      setError('Failed to delete driver.');
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await api.get('driver', {
        params: {
          all: 'ok',
          simple: 'ok',
        },
      });
      setDrivers(response.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (driver) => {
    try {
      const res = await api.post('driver_reset', { driverid: driver.id });
      const newPassword =
        res?.data?.password || res?.data?.newPassword || res?.data?.data?.password || 'Password generated';
      setResetDriver(driver);
      setResetPassword(newPassword);
      setResetDialogOpen(true);
    } catch (err) {
      console.error('Error resetting driver password:', err);
      window.alert('Unable to reset driver password. Please try again.');
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  return (
    <>
      <Helmet>
        <title>Drivers</title>
      </Helmet>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Drivers
          </Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            <MerchantFilterSelect
              merchants={merchantFilter.merchants}
              value={merchantFilter.selected}
              onChange={merchantFilter.setSelected}
              showFilter={merchantFilter.showFilter}
            />
            <Button
              variant="contained"
              onClick={() => {
                setEdit(false);
                setSelectedDriver(null);
                setModalNewDriverShow(true);
              }}
              startIcon={<Iconify icon="eva:plus-fill" />}
            >
              Create Driver
            </Button>
          </Stack>
        </Stack>

        <CreateDriverForm
          onCreateCallback={() => {
            setModalNewDriverShow(false);
            setLoading(true);
            fetchDrivers();
          }}
          modalShow={modalNewDriver}
          setModalShow={setModalNewDriverShow}
          edit={edit}
          driver={selectedDriver}
        />

        <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Password for&nbsp;
              <strong>{resetDriver?.firstname || 'driver'}</strong>
              &nbsp;has been reset to:&nbsp;
              <code>
                <strong>{resetPassword}</strong>
              </code>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetDialogOpen(false)} autoFocus>
              Okay
            </Button>
          </DialogActions>
        </Dialog>

        <Box sx={{ width: '100%', height: 400 }}>
          {loading ? (
            <Typography variant="h6" align="center">
              Loading...
            </Typography>
          ) : error ? (
            <Typography variant="h6" color="error" align="center">
              {error}
            </Typography>
          ) : (
            <DataGrid
              rows={withoutPlaceholder(merchantFilter.applyFilter(drivers))}
              columns={columns}
              pageSize={5}
            />
          )}
        </Box>
      </Container>
    </>
  );
}
